import { cloneElement, forwardRef, isValidElement, type InputHTMLAttributes, type ReactElement, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

interface FieldWrapperProps {
  label?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  htmlFor?: string;
  className?: string;
  children: ReactNode;
}

export function FieldWrapper({ label, hint, error, required, htmlFor, className, children }: FieldWrapperProps) {
  const hintId = htmlFor ? `${htmlFor}-hint` : undefined;
  const errorId = htmlFor ? `${htmlFor}-error` : undefined;
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {label && (
        <label htmlFor={htmlFor} className="text-sm font-medium text-fg">
          {label}
          {required && <span className="ml-0.5 text-gold-500">*</span>}
        </label>
      )}
      {children}
      {hint && !error && (
        <p id={hintId} className="text-xs text-fg-subtle">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" className="text-xs text-danger">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * Field wrapper that accepts either:
 *   - a single ReactElement (Input / Textarea / Select), or
 *   - a render-prop function `({ id, invalid }) => ReactNode` (legacy callsites).
 *
 * Only injects `invalid` into children that opt-in via our typed inputs
 * (Input / Textarea / Select). Plain DOM children like <a> or <code> would
 * otherwise leak `invalid` to the DOM and trigger React's "non-boolean
 * attribute" warning.
 */
interface FieldPropsBase {
  id: string;
  label?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  className?: string;
}

type FieldProps =
  | (FieldPropsBase & { children: ReactElement })
  | (FieldPropsBase & { children: (props: { id: string; invalid: boolean }) => ReactNode });

export function Field(props: FieldProps) {
  const { id, label, hint, error, required, className, children } = props;
  const invalid = !!error;
  let rendered: ReactNode;
  if (typeof children === 'function') {
    rendered = children({ id, invalid });
  } else if (isValidElement(children)) {
    const childType = (children as ReactElement).type;
    // Only inject `id` + `invalid` for our typed inputs. Anything else
    // (plain <a>, <code>, <div>, …) gets the id for the label association
    // but no `invalid` prop, so React doesn't emit the DOM warning.
    const acceptsInvalid =
      childType === Input || childType === Textarea || childType === Select;
    rendered = cloneElement(
      children as ReactElement<Record<string, unknown>>,
      acceptsInvalid ? { id, invalid } : { id },
    );
  } else {
    rendered = children;
  }
  return (
    <FieldWrapper
      label={label}
      hint={hint}
      error={error}
      required={required}
      htmlFor={id}
      className={className}
    >
      {rendered}
    </FieldWrapper>
  );
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }>(
  ({ className, invalid, ...props }, ref) => (
    <input
      ref={ref}
      className={cn(
        'flex h-10 w-full rounded-md border bg-bg-elevated px-3 text-sm text-fg placeholder:text-fg-subtle transition-colors focus:border-gold-500/60 focus:outline-none focus:ring-2 focus:ring-gold-500/20 disabled:cursor-not-allowed disabled:opacity-50',
        invalid ? 'border-danger/60' : 'border-border',
        className,
      )}
      {...props}
    />
  ),
);
Input.displayName = 'Input';

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }>(
  ({ className, invalid, ...props }, ref) => (
    <textarea
      ref={ref}
      className={cn(
        'flex min-h-[100px] w-full rounded-md border bg-bg-elevated px-3 py-2.5 text-sm text-fg placeholder:text-fg-subtle transition-colors focus:border-gold-500/60 focus:outline-none focus:ring-2 focus:ring-gold-500/20 disabled:cursor-not-allowed disabled:opacity-50',
        invalid ? 'border-danger/60' : 'border-border',
        className,
      )}
      {...props}
    />
  ),
);
Textarea.displayName = 'Textarea';

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement> & { invalid?: boolean }>(
  ({ className, invalid, children, ...props }, ref) => (
    <select
      ref={ref}
      className={cn(
        'flex h-10 w-full rounded-md border bg-bg-elevated px-3 text-sm text-fg transition-colors focus:border-gold-500/60 focus:outline-none focus:ring-2 focus:ring-gold-500/20 disabled:cursor-not-allowed disabled:opacity-50',
        invalid ? 'border-danger/60' : 'border-border',
        className,
      )}
      {...props}
    >
      {children}
    </select>
  ),
);
Select.displayName = 'Select';